import { BAYER, brightness, clamp01, hash2, type Scene } from "./noise.js";
import type { ArtPalette } from "./palette.js";

/** The grid a picture is laid on, in CSS pixels. */
export interface Grid {
  readonly width: number;
  readonly height: number;
  readonly dpr: number;
  readonly cell: number;
  readonly cols: number;
  readonly rows: number;
}

/**
 * A finished picture, off screen, the ramp step of every cell in it, and
 * whether the cell was drawn at all (a bare picture leaves its dim cells out).
 */
export interface Picture {
  readonly canvas: HTMLCanvasElement;
  readonly steps: Uint8Array;
  readonly drawn: Uint8Array;
}

/**
 * Where a bare picture sits and ends (DESIGN.md §9, "Pixel art", "Drops"): an
 * ellipse around its anchor, all in shares of the box. Its light is stretched
 * with it, so a wide, short box draws a streak rather than a dot.
 */
export interface Outline {
  /** The anchor, where the light is brightest: 0 is the left edge, 1 the right. */
  readonly x: number;
  /** 0 is the top edge, 1 the bottom. */
  readonly y: number;
  /** How far the picture reaches across, as a share of the box's width. */
  readonly rx: number;
  /** How far it reaches up or down, as a share of the box's height. */
  readonly ry: number;
}

/**
 * A picture cut to a shape rather than an outline: the cells to draw, one
 * per cell of the grid, and the palest ramp step any of them may take so the
 * shape reads solid. Bare, like an outline: nothing under the cells. Its
 * light is stretched across the whole box, from the scene's bright point.
 */
export interface Mask {
  readonly cells: Uint8Array;
  readonly floor: number;
}

/**
 * How a bare picture ends, as shares of the way out to its outline: solid to
 * the first, then ragged for the second, so it dissolves cell by cell at its
 * edge and nowhere else. Cutting by brightness instead left holes between the
 * arms, which read as white space, not as a picture (Abhishek, 2026-10-13).
 */
const BARE_SOLID = 0.72;
const BARE_RAG = 0.3;
/** The palest ramp step a bare picture draws, so no cell inside it is near-white. */
const BARE_MIN = 2;

function context(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("this browser gave no 2D canvas for the pixel art");
  return ctx;
}

/**
 * Paints one scene in one palette onto a canvas the size of the grid. Cut to
 * an outline or a mask the picture is bare: no background, and nothing past
 * the outline or outside the mask's cells.
 */
export function paintPicture(
  grid: Grid,
  palette: ArtPalette,
  scene: Scene,
  cut: Outline | Mask | null,
): Picture {
  const bare = cut !== null;
  const mask = cut !== null && "cells" in cut ? cut : null;
  const outline = cut !== null && !("cells" in cut) ? cut : null;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(grid.width * grid.dpr);
  canvas.height = Math.round(grid.height * grid.dpr);
  const g = context(canvas);
  g.scale(grid.dpr, grid.dpr);
  if (!bare) {
    g.fillStyle = palette.bg;
    g.fillRect(0, 0, grid.width, grid.height);
  }

  const last = palette.ramp.length - 1;
  const steps = new Uint8Array(grid.cols * grid.rows);
  const drawn = new Uint8Array(grid.cols * grid.rows);
  const reach = (Math.min(grid.width, grid.height) / 2) * scene.zoom;
  const light =
    mask === null
      ? lightOf(grid, scene, outline, reach)
      : {
          x: grid.width * scene.cx,
          y: grid.height * scene.cy,
          rx: (grid.width / 2) * scene.zoom,
          ry: (grid.height / 2) * scene.zoom,
        };
  for (let y = 0; y < grid.rows; y++) {
    for (let x = 0; x < grid.cols; x++) {
      const cx = (x + 0.5) * grid.cell;
      const cy = (y + 0.5) * grid.cell;
      const u = (cx - light.x) / light.rx;
      const v = (cy - light.y) / light.ry;
      const dither = (BAYER[(y & 3) * 4 + (x & 3)] ?? 0) * 0.95;
      let step = Math.min(
        last,
        Math.max(0, Math.floor(clamp01(brightness(u, v, scene)) * last + dither)),
      );
      if (mask !== null) {
        if (mask.cells[y * grid.cols + x] !== 1) {
          steps[y * grid.cols + x] = step;
          continue;
        }
        step = Math.max(step, mask.floor);
      } else if (bare) {
        if (Math.hypot(u, v) > BARE_SOLID + hash2(x, y, scene.seed + 17) * BARE_RAG) {
          steps[y * grid.cols + x] = step;
          continue;
        }
        step = Math.max(step, BARE_MIN);
      }
      steps[y * grid.cols + x] = step;
      drawn[y * grid.cols + x] = 1;
      const speckled = hash2(x, y, scene.seed + 9) < 0.022 && step > 1 && step < last - 2;
      g.fillStyle = speckled ? palette.speckle : (palette.ramp[step] ?? palette.bg);
      if (palette.shape === "square") {
        g.fillRect(x * grid.cell, y * grid.cell, grid.cell + 0.6, grid.cell + 0.6);
      } else {
        const radius = grid.cell * 0.5 * (0.22 + (0.7 * step) / last);
        g.beginPath();
        g.arc(cx, cy, radius, 0, Math.PI * 2);
        g.fill();
      }
    }
  }
  if (palette.shape === "square") {
    // On a bare picture the weave lands only on the cells, not the gaps.
    g.globalCompositeOperation = bare ? "source-atop" : "source-over";
    weave(g, grid, palette);
    g.globalCompositeOperation = "source-over";
  }
  return { canvas, steps, drawn };
}

/**
 * Where the light sits, in pixels, and how far it reaches each way: the
 * outline's anchor and radii, or the scene's own bright point and a circle.
 */
function lightOf(
  grid: Grid,
  scene: Scene,
  outline: Outline | null,
  reach: number,
): { readonly x: number; readonly y: number; readonly rx: number; readonly ry: number } {
  if (outline === null) {
    return { x: grid.width * scene.cx, y: grid.height * scene.cy, rx: reach, ry: reach };
  }
  return {
    x: grid.width * outline.x,
    y: grid.height * outline.y,
    rx: Math.max(1, grid.width * outline.rx),
    ry: Math.max(1, grid.height * outline.ry),
  };
}

/** A woven texture over square cells: fine dots and a faint grid. */
function weave(g: CanvasRenderingContext2D, grid: Grid, palette: ArtPalette): void {
  g.globalAlpha = 0.075;
  g.fillStyle = palette.white;
  for (let yy = 1; yy < grid.height; yy += 3) {
    for (let xx = 1; xx < grid.width; xx += 3) {
      if (Math.floor((xx + yy) / 3) % 2 === 0) g.fillRect(xx, yy, 1, 1);
    }
  }
  g.globalAlpha = 0.1;
  g.fillStyle = palette.ink;
  for (let x = 1; x < grid.cols; x++)
    g.fillRect(x * grid.cell - 0.25, 0, 0.5, grid.height);
  for (let y = 1; y < grid.rows; y++)
    g.fillRect(0, y * grid.cell - 0.25, grid.width, 0.5);
  g.globalAlpha = 1;
}
